import React, { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  styled,
  Switch,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { linearProgressClasses } from "@mui/material/LinearProgress";
import { Link as RouterLink } from "react-router-dom";
import {
  getAllInstallersStatistics,
  selectAllInstallersStatisticsState,
} from "pages/dashboard/statisticsSlice";
import Spinner from "features/spinner/Spinner";
import Section from "components/Section";
import SectionHeader from "components/SectionHeader";
import OutlinedCard from "components/OutlinedCard";
import { TimePeriod, TimePeriodValues } from "models/UserStatistics";
import StyledTable from "components/StyledTable";

function normalize(value: number, max: number): number {
  return (value * 100) / max;
}

export enum InstallerTypeFilter {
  ALL,
  INTERNAL_INSTALLER,
  EXTERNAL_INSTALLER,
}

export function getInstallerTypeFilterString(value: InstallerTypeFilter): string {
  switch (value) {
    case InstallerTypeFilter.ALL:
      return "Alla installatörer";
    case InstallerTypeFilter.INTERNAL_INSTALLER:
      return "Interna installatörer";
    case InstallerTypeFilter.EXTERNAL_INSTALLER:
      return "Externa installatörer";
  }
}

const ProgressBar = styled(LinearProgress)(({ theme }) => ({
  height: "1em",
  borderRadius: 3,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: "transparent",
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 3,
    backgroundColor: theme.palette.primary.main,
  },
}));

const TopInstallers: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    isLoading,
    loaded: isLoaded,
    allInstallerStatistics: topInstallers,
  } = useAppSelector(selectAllInstallersStatisticsState);

  // Filter values
  const [installerType, setInstallerType] = useState<InstallerTypeFilter>(InstallerTypeFilter.ALL);
  const [timePeriod, setTimePeriod] = useState<TimePeriod>("FROM_START");
  const [onlyApproved, setOnlyApproved] = useState<boolean>(true);

  useEffect(() => {
    dispatch(
      getAllInstallersStatistics({
        timePeriod: timePeriod,
        installerType: InstallerTypeFilter[installerType],
      })
    );
  }, [timePeriod, installerType]);

  const sortedTopInstallers = useMemo(() => {
    return [...topInstallers]
      .sort(
        onlyApproved
          ? (a, b) => b.numberOfApprovedChecklists - a.numberOfApprovedChecklists
          : (a, b) => b.numberOfCompletedChecklists - a.numberOfCompletedChecklists
      )
      .slice(0, 5);
  }, [topInstallers, onlyApproved]);

  const maxChecklists = useMemo(() => {
    return onlyApproved
      ? Math.max(...topInstallers.map((user) => user.numberOfApprovedChecklists))
      : Math.max(...topInstallers.map((user) => user.numberOfCompletedChecklists));
  }, [onlyApproved, topInstallers]);

  return (
    <Section display="flex" flexDirection="column" flexGrow={1}>
      <SectionHeader title="Topp 5" />

      <OutlinedCard flexGrow={1} display="flex" flexDirection="column">
        <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
          <FormControl variant="filled" size="small" sx={{ minWidth: 150, flexGrow: 0 }}>
            <InputLabel>Installatörer</InputLabel>
            <Select
              value={installerType}
              onChange={(ev) => setInstallerType(ev.target.value as InstallerTypeFilter)}
            >
              <MenuItem value={InstallerTypeFilter.ALL}>Alla</MenuItem>
              <MenuItem value={InstallerTypeFilter.INTERNAL_INSTALLER}>Interna</MenuItem>
              <MenuItem value={InstallerTypeFilter.EXTERNAL_INSTALLER}>Externa</MenuItem>
            </Select>
          </FormControl>

          <FormControl variant="filled" size="small" sx={{ minWidth: 200, flexGrow: 0 }}>
            <InputLabel>Period</InputLabel>
            <Select
              value={timePeriod}
              onChange={(ev) => setTimePeriod(ev.target.value as TimePeriod)}
            >
              <MenuItem value="FROM_START">{TimePeriodValues.FROM_START}</MenuItem>
              <MenuItem value="PAST_WEEK">{TimePeriodValues.PAST_WEEK}</MenuItem>
              <MenuItem value="PAST_MONTH">{TimePeriodValues.PAST_MONTH}</MenuItem>
              <MenuItem value="PAST_YEAR">{TimePeriodValues.PAST_YEAR}</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            label="Endast godkända"
            control={
              <Switch checked={onlyApproved} onChange={() => setOnlyApproved(!onlyApproved)} />
            }
          />
        </Box>

        {isLoading && (
          <Box display="grid" flexGrow={1} sx={{ placeItems: "center" }}>
            <Spinner color="#5f5f5f80" size="3rem" />
          </Box>
        )}

        {!isLoading && isLoaded && sortedTopInstallers.length > 0 && (
          <StyledTable size="small" noBottomBorder>
            <TableHead>
              <TableRow sx={{ fontWeight: "bold" }}>
                <TableCell sx={{ fontWeight: "bold", width: "2em" }}>#</TableCell>
                <TableCell sx={{ fontWeight: "bold", width: "auto" }}>Installatör</TableCell>
                <TableCell sx={{ fontWeight: "bold", width: "60%" }}>Checklistor</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {sortedTopInstallers?.map((user, index) => (
                <TableRow key={index}>
                  <TableCell>{index + 1}.</TableCell>
                  <TableCell>{user.userName}</TableCell>
                  <TableCell>
                    <Box display="flex" gap={1} alignItems="center">
                      <ProgressBar
                        variant="determinate"
                        color="primary"
                        sx={{ flexGrow: 1 }}
                        value={normalize(
                          onlyApproved
                            ? user.numberOfApprovedChecklists
                            : user.numberOfCompletedChecklists,
                          maxChecklists
                        )}
                      />
                      <Typography>
                        {onlyApproved
                          ? user.numberOfApprovedChecklists
                          : user.numberOfCompletedChecklists}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </StyledTable>
        )}

        {!isLoading && isLoaded && sortedTopInstallers.length < 0 && <p>Ej tillgänglig</p>}

        <Box display="flex" justifyContent="flex-end" marginTop="auto">
          <Button component={RouterLink} to="/installer-statistics">
            Visa alla
          </Button>
        </Box>
      </OutlinedCard>
    </Section>
  );
};

export default TopInstallers;
